import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';

const SIN_VINCULO = '__ninguno__';
const numDe = (c) => c.numero || c.numero_limpio || c.cvu;
const esActivo = (c) => c.activo !== undefined ? !!c.activo : ['activo', 'habilitado'].includes((c.estado || '').toLowerCase());

export const VincularReemplazoDialog = ({ open, onOpenChange, cuit, cvu, cvus, onSaved }) => {
  const [anterior, setAnterior] = useState(SIN_VINCULO);
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && cvu) {
      setAnterior(cvu.reemplaza_a || SIN_VINCULO);
      setMotivo(cvu.motivo_cambio || '');
    }
  }, [open, cvu]);

  if (!cvu) return null;
  const otros = (cvus || []).filter((c) => numDe(c) !== numDe(cvu));
  const elegido = otros.find((c) => numDe(c) === anterior);

  const guardar = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/padron-interno/cliente/${cuit}/cvu/${numDe(cvu)}/reemplazo`, {
        reemplaza_a: anterior === SIN_VINCULO ? null : anterior, motivo_cambio: motivo,
      });
      if (res.data?.advertencia) toast.warning(`Vínculo guardado: ${res.data.advertencia}`);
      else toast.success(anterior === SIN_VINCULO ? 'Vínculo quitado' : 'Reemplazo vinculado');
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar el reemplazo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="vincular-reemplazo-dialog">
        <DialogHeader>
          <DialogTitle>Vincular reemplazo</DialogTitle>
          <DialogDescription>
            El CVU <span className="font-mono">{numDe(cvu)}</span> reemplaza a otro CVU del mismo CUIT. El estado del anterior no se modifica.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>CVU anterior</Label>
            <Select value={anterior} onValueChange={setAnterior}>
              <SelectTrigger className="mt-1 font-mono text-xs" data-testid="vincular-reemplazo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SIN_VINCULO}>Sin vínculo</SelectItem>
                {otros.map((c) => (
                  <SelectItem key={numDe(c)} value={numDe(c)} className="font-mono text-xs">
                    {numDe(c)} · {c.banco_sponsor?.nombre || 'sponsor desconocido'}{esActivo(c) ? ' · activo' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {elegido && esActivo(elegido) && (
            <p className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="vincular-reemplazo-advertencia">
              <AlertTriangle size={13} /> el CVU anterior sigue activo (se vincula igual)
            </p>
          )}
          <div>
            <Label>Motivo del cambio</Label>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej.: Cambio de banco sponsor CMF → BI" className="mt-1" data-testid="vincular-reemplazo-motivo" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vincular-reemplazo-cancelar">Cancelar</Button>
          <Button onClick={guardar} disabled={saving} data-testid="vincular-reemplazo-guardar">{saving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
